import React, { forwardRef } from 'react';
import { Button, IconButton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { borderRadius, getControlBorderColor, layoutGap } from '../../../theme/theme';
import ActionHelpTooltip from '../common/ActionHelpTooltip';
import type { HelpValue } from '../common/helpModel';
import { getWorkflowChromePalette } from './WorkflowStepComponents';

export interface ResponsiveToolbarButtonProps {
  icon: React.ReactNode;
  label: string;
  'aria-label'?: string;
  showText?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'inherit';
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  help?: HelpValue;
  disabledHelp?: HelpValue;
  size?: 'small' | 'medium';
  ref?: React.Ref<HTMLButtonElement>;
  'data-testid'?: string;
  sx?: SxProps<Theme>;
}

const ResponsiveToolbarButton = forwardRef<HTMLButtonElement, ResponsiveToolbarButtonProps>(
  function ResponsiveToolbarButton(
    {
      icon,
      label,
      'aria-label': ariaLabel,
      showText = true,
      variant = 'outlined',
      color = 'primary',
      active = false,
      disabled = false,
      onClick,
      help,
      disabledHelp,
      size = 'medium',
      'data-testid': dataTestId,
      sx,
    },
    ref
  ) {
    const theme = useTheme();
    const workflowPalette = getWorkflowChromePalette(theme, 'workspace');

    const height = size === 'small' ? 28 : 30;
    const radius = `${borderRadius.md}px`;
    const paletteColor = color === 'inherit'
      ? theme.palette.text.primary
      : theme.palette[color].main;
    const hoverColor = color === 'inherit'
      ? theme.palette.action.hover
      : theme.palette[color].dark;
    const contrastColor = color === 'inherit'
      ? theme.palette.background.paper
      : theme.palette[color].contrastText;

    const activeSx: SxProps<Theme> = active
      ? {
          backgroundColor: paletteColor,
          color: contrastColor,
          borderColor: paletteColor,
          '&:hover': {
            backgroundColor: hoverColor,
            borderColor: paletteColor,
          },
        }
      : {};

    const iconButton = (
      <IconButton
        ref={ref}
        size="small"
        disabled={disabled}
        onClick={onClick}
        aria-label={ariaLabel ?? label}
        aria-pressed={active}
        data-testid={dataTestId}
        sx={[
          {
            width: height,
            height: height,
            minWidth: height,
            minHeight: height,
            p: 0,
            borderRadius: `${borderRadius.sm}px`,
            border: `1px solid ${getControlBorderColor(theme)}`,
            backgroundColor: workflowPalette.tileBackground,
            color: paletteColor,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
              borderColor: getControlBorderColor(theme),
            },
            '&.Mui-disabled': {
              opacity: 0.3,
              borderColor: getControlBorderColor(theme),
            },
          },
          activeSx,
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        {icon}
      </IconButton>
    );

    const button = (
      <Button
        ref={ref}
        size="small"
        variant={variant}
        color={color}
        disabled={disabled}
        onClick={onClick}
        aria-label={ariaLabel ?? label}
        aria-pressed={active}
        startIcon={icon}
        data-testid={dataTestId}
        sx={[
          {
            height,
            minHeight: height,
            maxHeight: height,
            fontSize: size === 'small' ? '0.65rem' : '0.7rem',
            px: 0.75,
            borderRadius: radius,
            boxShadow: 'none',
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: layoutGap.dense,
              ml: 0,
            },
            '& .MuiButton-startIcon > *:nth-of-type(1)': {
              fontSize: '1rem',
            },
          },
          activeSx,
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        {label}
      </Button>
    );

    const content = showText ? button : iconButton;

    if (!help && !disabledHelp) {
      return content;
    }

    return (
      <ActionHelpTooltip help={help} disabledHelp={disabledHelp} disabled={disabled}>
        {content}
      </ActionHelpTooltip>
    );
  }
);

ResponsiveToolbarButton.displayName = 'ResponsiveToolbarButton';

export default ResponsiveToolbarButton;
